import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { LoadingContainer, InlineSpinner, ButtonSpinner } from '../components/ui/Spinner';
import { ArrowLeftIcon, PencilIcon, CameraIcon, TrashIcon, XMarkIcon, PlusIcon } from '@heroicons/react/24/outline';
import { handleImageError } from '../utils/imageUtils';
import PlayerTypeIcon, {
    VS_AGGRESSION_OPTIONS,
    AGGRESSION_STYLE_OPTIONS,
    PREFLOP_HANDS_OPTIONS,
    PREFLOP_AGGRESSION_OPTIONS,
    MatrixBadge
} from '../components/PlayerTypeIcon';

// Category display names for tendency tags
const CATEGORY_LABELS = {
    preflop: 'Preflop',
    postflop: 'Postflop',
    bet_sizing: 'Bet Sizing',
    mental: 'Mental/Emotional',
    live_tell: 'Live Tell',
    other: 'Other',
};

const TENDENCY_THRESHOLD = 3;

function PlayerDetailsPage() {
    const { playerId } = useParams();
    const navigate = useNavigate();
    const [player, setPlayer] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editing, setEditing] = useState(false);
    const [editForm, setEditForm] = useState({
        name: '',
        notes: '',
        player_category: 'unknown',
        preflop_hands: 'unknown',
        preflop_aggression: 'unknown',
        vs_aggression: 'unknown',
        aggression_style: 'unknown',
    });
    const [uploadingImage, setUploadingImage] = useState(false);
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [recordingTendency, setRecordingTendency] = useState(null);
    const [showTendencyPicker, setShowTendencyPicker] = useState(false);
    const [tendencyTags, setTendencyTags] = useState([]);
    const [newTagName, setNewTagName] = useState('');
    const [newTagCategory, setNewTagCategory] = useState('other');
    const [creatingTag, setCreatingTag] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState('all');

    // Player notes state
    const [playerNotes, setPlayerNotes] = useState([]);
    const [newNoteContent, setNewNoteContent] = useState('');
    const [addingNote, setAddingNote] = useState(false);
    const [showAddNote, setShowAddNote] = useState(false);

    // Player hands state
    const [playerHands, setPlayerHands] = useState([]);
    const [handsLoading, setHandsLoading] = useState(false);

    const fetchPlayerNotes = useCallback(async () => {
        try {
            const response = await axios.get(`/api/player-notes/?player=${playerId}`);
            setPlayerNotes(response.data);
        } catch (err) {
            console.error('Failed to fetch player notes:', err);
        }
    }, [playerId]);

    const fetchPlayerHands = useCallback(async () => {
        setHandsLoading(true);
        try {
            const response = await axios.get(`/api/players/${playerId}/hands/`);
            setPlayerHands(response.data);
        } catch (err) {
            console.error('Failed to fetch player hands:', err);
        } finally {
            setHandsLoading(false);
        }
    }, [playerId]);

    const handleAddNote = async () => {
        if (!newNoteContent.trim()) return;
        setAddingNote(true);
        try {
            await axios.post('/api/player-notes/', {
                player: playerId,
                content: newNoteContent.trim()
            });
            setNewNoteContent('');
            setShowAddNote(false);
            await fetchPlayerNotes();
        } catch (err) {
            console.error('Failed to add note:', err);
        } finally {
            setAddingNote(false);
        }
    };

    const handleDeleteNote = async (noteId) => {
        try {
            await axios.delete(`/api/player-notes/${noteId}/`);
            await fetchPlayerNotes();
        } catch (err) {
            console.error('Failed to delete note:', err);
        }
    };

    const fetchPlayerDetails = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`/api/players/${playerId}/`);
            setPlayer(response.data);
            setEditForm({
                name: response.data.name || '',
                notes: response.data.notes || '',
                player_category: response.data.player_category || 'unknown',
                preflop_hands: response.data.preflop_hands || 'unknown',
                preflop_aggression: response.data.preflop_aggression || 'unknown',
                vs_aggression: response.data.vs_aggression || 'unknown',
                aggression_style: response.data.aggression_style || 'unknown',
            });
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to fetch player details.');
        } finally {
            setLoading(false);
        }
    }, [playerId]);

    const fetchTendencyTags = useCallback(async () => {
        try {
            const response = await axios.get('/api/tendency-tags/');
            setTendencyTags(response.data);
        } catch (err) {
            console.error('Failed to fetch tendency tags:', err);
        }
    }, []);

    useEffect(() => {
        fetchPlayerDetails();
        fetchTendencyTags();
        fetchPlayerNotes();
        fetchPlayerHands();
    }, [fetchPlayerDetails, fetchTendencyTags, fetchPlayerNotes, fetchPlayerHands]);

    // Get tendency label from tags
    const getTendencyLabel = useCallback((key) => {
        const tag = tendencyTags.find(t => t.key === key);
        return tag ? tag.name : key;
    }, [tendencyTags]);

    // Group tendency tags by category
    const groupedTags = tendencyTags.reduce((acc, tag) => {
        if (!acc[tag.category]) acc[tag.category] = [];
        acc[tag.category].push(tag);
        return acc;
    }, {});

    // Handle creating a new tendency tag
    const handleCreateTag = async () => {
        if (!newTagName.trim()) return;

        setCreatingTag(true);
        try {
            const response = await axios.post('/api/tendency-tags/quick_create/', {
                name: newTagName.trim(),
                category: newTagCategory
            });
            // Add to local state
            setTendencyTags(prev => [...prev, response.data]);
            // Auto-record this tendency for the player
            await handleRecordTendency(response.data.key);
            setNewTagName('');
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to create tendency tag.');
        } finally {
            setCreatingTag(false);
        }
    };

    const handleEditSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.patch(`/api/players/${playerId}/`, editForm);
            setPlayer(response.data);
            setEditing(false);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to update player.');
        }
    };

    const handleRecordTendency = async (tendencyKey) => {
        setRecordingTendency(tendencyKey);
        try {
            await axios.post(`/api/players/${playerId}/record-tendency/`, {
                tendency: tendencyKey
            });
            // Refresh player data
            await fetchPlayerDetails();
            setShowTendencyPicker(false);
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to record tendency.');
        } finally {
            setRecordingTendency(null);
        }
    };

    const handleRemoveTendency = async (tendencyKey) => {
        try {
            await axios.delete(`/api/players/${playerId}/remove-tendency/${tendencyKey}/`);
            await fetchPlayerDetails();
        } catch (err) {
            setError(err.response?.data?.error || 'Failed to remove tendency.');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setError('Please select a valid image file.');
            return;
        }

        if (file.size > 5 * 1024 * 1024) {
            setError('Image file size must be less than 5MB.');
            return;
        }

        const formData = new FormData();
        formData.append('profile_picture', file);

        setUploadingImage(true);
        try {
            const response = await axios.patch(`/api/players/${playerId}/`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setPlayer(prev => ({
                ...prev,
                profile_picture: response.data.profile_picture,
                profile_picture_url: response.data.profile_picture_url
            }));
            setError(null);
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to upload image.');
        } finally {
            setUploadingImage(false);
        }
    };

    const handleDeleteConfirm = async () => {
        setDeleteLoading(true);
        try {
            await axios.delete(`/api/players/${playerId}/`);
            navigate('/players');
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete player.');
        } finally {
            setDeleteLoading(false);
            setShowDeleteConfirm(false);
        }
    };

    const formatDateTime = (dateTimeStr) => {
        return new Date(dateTimeStr).toLocaleString();
    };

    if (loading) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <LoadingContainer />
            </div>
        );
    }

    if (error && !player) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                    {error}
                </div>
            </div>
        );
    }

    if (!player) {
        return (
            <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
                <div className="text-center py-8">
                    <p className="text-gray-600 dark:text-gray-400">Player not found.</p>
                </div>
            </div>
        );
    }

    const tendencies = player.tendencies || {};
    const confirmedTendencies = player.confirmed_tendencies || {};

    return (
        <div className="flex-grow px-4 py-4 mx-auto w-full max-w-6xl">
            {/* Header */}
            <div className="flex items-center gap-4 mb-4 sm:mb-6">
                <button
                    onClick={() => navigate('/players')}
                    className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                >
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                    Player Profile
                </h1>
            </div>

            {/* Error Alert */}
            {error && (
                <div className="mb-4 p-4 rounded-lg bg-red-50 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800">
                    {error}
                    <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
                </div>
            )}

            {/* Player Info Card */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                        {/* Profile Picture */}
                        <div className="relative">
                            <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold bg-primary-600 text-white">
                                {(player.profile_picture_url || player.profile_picture) ? (
                                    <img
                                        src={player.profile_picture_url || player.profile_picture}
                                        alt={player.name}
                                        className="w-full h-full rounded-full object-cover cursor-pointer hover:opacity-80 transition-opacity"
                                        onClick={() => setShowImageModal(true)}
                                        onError={(e) => handleImageError(e, player.name?.[0] || '?')}
                                    />
                                ) : (
                                    player.name?.[0] || '?'
                                )}
                            </div>
                            <label className="absolute -bottom-1 -right-1 w-6 h-6 bg-primary-600 text-white rounded-full flex items-center justify-center cursor-pointer hover:bg-primary-700 transition-colors">
                                <CameraIcon className="h-3 w-3" />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageUpload}
                                    className="hidden"
                                    disabled={uploadingImage}
                                />
                            </label>
                            {uploadingImage && (
                                <div className="absolute inset-0 bg-black bg-opacity-50 rounded-full flex items-center justify-center">
                                    <InlineSpinner />
                                </div>
                            )}
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <PlayerTypeIcon playerType={player.player_category} size="w-4 h-4" />
                                <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                                    {player.name}
                                </h2>
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                Updated: {formatDateTime(player.updated_at)}
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setEditing(!editing)}
                            className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                            onClick={() => setShowDeleteConfirm(true)}
                            className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50 transition-colors"
                        >
                            <TrashIcon className="h-5 w-5" />
                        </button>
                    </div>
                </div>

                {/* Edit Form */}
                {editing && (
                    <form onSubmit={handleEditSubmit} className="mb-6 p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Name
                                </label>
                                <input
                                    type="text"
                                    value={editForm.name}
                                    onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    required
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Category
                                </label>
                                <select
                                    value={editForm.player_category}
                                    onChange={(e) => setEditForm({ ...editForm, player_category: e.target.value })}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none"
                                >
                                    <option value="unknown">Unknown</option>
                                    <option value="fish">Fish</option>
                                    <option value="whale">Whale</option>
                                    <option value="nit">Nit</option>
                                    <option value="reg">Reg</option>
                                    <option value="shark">Shark</option>
                                </select>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                    Notes
                                </label>
                                <textarea
                                    value={editForm.notes}
                                    onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                                    className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 dark:focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-500"
                                    placeholder="Hand observations, tendencies, exploit strategies..."
                                    rows={4}
                                />
                                <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    Record specific hands, long-term patterns, and exploit strategies
                                </p>
                            </div>
                        </div>

                        {/* Preflop Framework */}
                        <div className="mb-4">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Preflop Profile
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Hand Selection</label>
                                    <select
                                        value={editForm.preflop_hands}
                                        onChange={(e) => setEditForm({ ...editForm, preflop_hands: e.target.value })}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none"
                                    >
                                        {Object.entries(PREFLOP_HANDS_OPTIONS).map(([key, config]) => (
                                            <option key={key} value={key}>{config.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Aggression</label>
                                    <select
                                        value={editForm.preflop_aggression}
                                        onChange={(e) => setEditForm({ ...editForm, preflop_aggression: e.target.value })}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none"
                                    >
                                        {Object.entries(PREFLOP_AGGRESSION_OPTIONS).map(([key, config]) => (
                                            <option key={key} value={key}>{config.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Postflop Matrix */}
                        <div className="mb-4">
                            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                Postflop Profile
                            </h3>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">vs Aggression</label>
                                    <select
                                        value={editForm.vs_aggression}
                                        onChange={(e) => setEditForm({ ...editForm, vs_aggression: e.target.value })}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none"
                                    >
                                        {Object.entries(VS_AGGRESSION_OPTIONS).map(([key, config]) => (
                                            <option key={key} value={key}>{config.label}</option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Aggression Style</label>
                                    <select
                                        value={editForm.aggression_style}
                                        onChange={(e) => setEditForm({ ...editForm, aggression_style: e.target.value })}
                                        className="block w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-gray-900 dark:text-white focus:border-primary-500 focus:outline-none"
                                    >
                                        {Object.entries(AGGRESSION_STYLE_OPTIONS).map(([key, config]) => (
                                            <option key={key} value={key}>{config.label}</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <button
                                type="submit"
                                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                            >
                                Save Changes
                            </button>
                            <button
                                type="button"
                                onClick={() => setEditing(false)}
                                className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-400 dark:hover:bg-gray-500 transition-colors"
                            >
                                Cancel
                            </button>
                        </div>
                    </form>
                )}

                {/* Matrix Display */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Preflop */}
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Preflop</h3>
                        <div className="flex flex-wrap gap-2">
                            <MatrixBadge value={player.preflop_hands} options={PREFLOP_HANDS_OPTIONS} size="md" />
                            <MatrixBadge value={player.preflop_aggression} options={PREFLOP_AGGRESSION_OPTIONS} size="md" />
                        </div>
                    </div>

                    {/* Postflop */}
                    <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Postflop</h3>
                        <div className="flex flex-wrap gap-2">
                            <MatrixBadge value={player.vs_aggression} options={VS_AGGRESSION_OPTIONS} size="md" />
                            <MatrixBadge value={player.aggression_style} options={AGGRESSION_STYLE_OPTIONS} size="md" />
                        </div>
                    </div>
                </div>

            </div>

            {/* Notes Section - Multiple entries */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Notes ({playerNotes.length})
                    </h2>
                    <button
                        onClick={() => setShowAddNote(!showAddNote)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Add Note
                    </button>
                </div>

                {/* Add Note Form */}
                {showAddNote && (
                    <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        <textarea
                            value={newNoteContent}
                            onChange={(e) => setNewNoteContent(e.target.value)}
                            placeholder="Hand observation, tendency, exploit strategy..."
                            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                            rows={3}
                            autoFocus
                        />
                        <div className="flex justify-end gap-2 mt-2">
                            <button
                                onClick={() => { setShowAddNote(false); setNewNoteContent(''); }}
                                className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddNote}
                                disabled={addingNote || !newNoteContent.trim()}
                                className="px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 text-sm"
                            >
                                {addingNote ? 'Adding...' : 'Add'}
                            </button>
                        </div>
                    </div>
                )}

                {/* Notes List */}
                {playerNotes.length > 0 ? (
                    <div className="space-y-3">
                        {playerNotes.map(note => (
                            <div key={note.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg group">
                                <div className="flex justify-between items-start gap-2">
                                    <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap flex-1">{note.content}</p>
                                    <button
                                        onClick={() => handleDeleteNote(note.id)}
                                        className="text-gray-400 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                                        title="Delete note"
                                    >
                                        <TrashIcon className="h-4 w-4" />
                                    </button>
                                </div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                    {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No notes yet. Add observations about hands, tendencies, and exploit strategies.
                    </p>
                )}
            </div>

            {/* Hands History */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    Hands Played ({playerHands.length})
                </h2>

                {handsLoading ? (
                    <div className="flex justify-center py-4">
                        <InlineSpinner />
                    </div>
                ) : playerHands.length > 0 ? (
                    <div className="space-y-3">
                        {playerHands.map(hand => (
                            <div key={hand.id} className="p-3 bg-gray-50 dark:bg-gray-700/50 rounded-lg border border-gray-200 dark:border-gray-600">
                                <div className="flex items-start justify-between gap-2 mb-2">
                                    <div className="flex-1">
                                        <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                                            {hand.hand_description}
                                        </p>
                                    </div>
                                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                        hand.outcome === 'won' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' :
                                        hand.outcome === 'lost' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' :
                                        'bg-gray-100 text-gray-700 dark:bg-gray-600 dark:text-gray-300'
                                    }`}>
                                        {hand.outcome_display} ${Math.abs(hand.amount)}
                                    </span>
                                </div>
                                {/* Tags */}
                                {hand.tags_display && hand.tags_display.length > 0 && (
                                    <div className="flex flex-wrap gap-1 mb-2">
                                        {hand.tags_display.map((tag, idx) => (
                                            <span key={idx} className="px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded text-xs">
                                                {tag}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {/* Notes */}
                                {hand.notes && (
                                    <p className="text-xs text-gray-500 dark:text-gray-400 italic mb-1">
                                        {hand.notes}
                                    </p>
                                )}
                                <p className="text-xs text-gray-400 dark:text-gray-500">
                                    {new Date(hand.timestamp).toLocaleDateString()} {new Date(hand.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                                </p>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500 dark:text-gray-400 italic">
                        No hands recorded with this player yet.
                    </p>
                )}
            </div>

            {/* Tendency Tracking */}
            <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4 sm:p-6 mb-4 sm:mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                        Tendency Observations
                    </h2>
                    <button
                        onClick={() => setShowTendencyPicker(!showTendencyPicker)}
                        className="flex items-center gap-1 px-3 py-1.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm"
                    >
                        <PlusIcon className="h-4 w-4" />
                        Record
                    </button>
                </div>

                <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    Tendencies are confirmed after {TENDENCY_THRESHOLD} observations. Only confirmed tendencies affect exploit strategy.
                </p>

                {/* Tendency Picker */}
                {showTendencyPicker && (
                    <div className="mb-4 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                        {/* Category Filter */}
                        <div className="flex flex-wrap gap-2 mb-4">
                            <button
                                onClick={() => setSelectedCategory('all')}
                                className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                    selectedCategory === 'all'
                                        ? 'bg-primary-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                }`}
                            >
                                All
                            </button>
                            {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                                <button
                                    key={key}
                                    onClick={() => setSelectedCategory(key)}
                                    className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                                        selectedCategory === key
                                            ? 'bg-primary-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                                    }`}
                                >
                                    {label}
                                </button>
                            ))}
                        </div>

                        {/* Create New Tag */}
                        <div className="mb-4 p-3 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600">
                            <h4 className="text-xs font-medium text-gray-600 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                Create New Tendency
                            </h4>
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newTagName}
                                    onChange={(e) => setNewTagName(e.target.value)}
                                    placeholder="e.g., Calls river light"
                                    className="flex-1 px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                />
                                <select
                                    value={newTagCategory}
                                    onChange={(e) => setNewTagCategory(e.target.value)}
                                    className="px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                    {Object.entries(CATEGORY_LABELS).map(([key, label]) => (
                                        <option key={key} value={key}>{label}</option>
                                    ))}
                                </select>
                                <button
                                    onClick={handleCreateTag}
                                    disabled={creatingTag || !newTagName.trim()}
                                    className="px-3 py-1.5 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 text-sm"
                                >
                                    {creatingTag ? <InlineSpinner /> : '+ Add'}
                                </button>
                            </div>
                        </div>

                        {/* Existing Tags by Category */}
                        <div className="space-y-3">
                            {(selectedCategory === 'all' ? Object.keys(CATEGORY_LABELS) : [selectedCategory]).map(category => {
                                const categoryTags = groupedTags[category] || [];
                                if (categoryTags.length === 0) return null;

                                return (
                                    <div key={category}>
                                        <h4 className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-2 uppercase tracking-wide">
                                            {CATEGORY_LABELS[category]}
                                        </h4>
                                        <div className="flex flex-wrap gap-2">
                                            {categoryTags.map((tag) => (
                                                <button
                                                    key={tag.key}
                                                    onClick={() => handleRecordTendency(tag.key)}
                                                    disabled={recordingTendency === tag.key}
                                                    className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-full text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
                                                >
                                                    {recordingTendency === tag.key ? <InlineSpinner /> : tag.name}
                                                </button>
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* Current Tendencies */}
                {Object.keys(tendencies).length > 0 ? (
                    <div className="space-y-3">
                        {/* Confirmed */}
                        {Object.keys(confirmedTendencies).length > 0 && (
                            <div>
                                <h4 className="text-xs font-medium text-green-600 dark:text-green-400 mb-2 uppercase tracking-wide">
                                    Confirmed ({Object.keys(confirmedTendencies).length})
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(confirmedTendencies).map(([key, data]) => (
                                        <div
                                            key={key}
                                            className="flex items-center gap-2 px-3 py-1.5 bg-green-100 dark:bg-green-900/30 border border-green-300 dark:border-green-700 rounded-full"
                                        >
                                            <span className="text-sm text-green-800 dark:text-green-200">
                                                {getTendencyLabel(key)} ({data.count}x)
                                            </span>
                                            <button
                                                onClick={() => handleRemoveTendency(key)}
                                                className="text-green-600 dark:text-green-400 hover:text-red-600 dark:hover:text-red-400"
                                            >
                                                <XMarkIcon className="h-4 w-4" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Unconfirmed */}
                        {Object.entries(tendencies).filter(([key, data]) => !data.confirmed).length > 0 && (
                            <div>
                                <h4 className="text-xs font-medium text-orange-600 dark:text-orange-400 mb-2 uppercase tracking-wide">
                                    Tracking (need {TENDENCY_THRESHOLD} to confirm)
                                </h4>
                                <div className="flex flex-wrap gap-2">
                                    {Object.entries(tendencies)
                                        .filter(([key, data]) => !data.confirmed)
                                        .map(([key, data]) => (
                                            <div
                                                key={key}
                                                className="flex items-center gap-2 px-3 py-1.5 bg-orange-100 dark:bg-orange-900/30 border border-orange-300 dark:border-orange-700 rounded-full"
                                            >
                                                <span className="text-sm text-orange-800 dark:text-orange-200">
                                                    {getTendencyLabel(key)} ({data.count}/{TENDENCY_THRESHOLD})
                                                </span>
                                                <button
                                                    onClick={() => handleRecordTendency(key)}
                                                    className="text-orange-600 dark:text-orange-400 hover:text-green-600 dark:hover:text-green-400 font-bold"
                                                    title="Record another observation"
                                                >
                                                    +
                                                </button>
                                                <button
                                                    onClick={() => handleRemoveTendency(key)}
                                                    className="text-orange-600 dark:text-orange-400 hover:text-red-600 dark:hover:text-red-400"
                                                >
                                                    <XMarkIcon className="h-4 w-4" />
                                                </button>
                                            </div>
                                        ))}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p>No tendencies recorded yet.</p>
                        <p className="text-sm mt-1">Click "Record" to start tracking patterns you observe.</p>
                    </div>
                )}
            </div>

            {/* Image Modal */}
            {showImageModal && (player.profile_picture_url || player.profile_picture) && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-75 flex items-start justify-center z-50 p-4 pt-8 overflow-y-auto"
                    onClick={() => setShowImageModal(false)}
                >
                    <div className="relative max-w-4xl max-h-full">
                        <img
                            src={player.profile_picture_url || player.profile_picture}
                            alt={player.name}
                            className="max-w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                        />
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            {showDeleteConfirm && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center z-50 pt-8 overflow-y-auto">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl max-w-md mx-4">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                            Delete Player?
                        </h3>
                        <p className="text-gray-700 dark:text-gray-300 mb-6">
                            Are you sure you want to delete "{player.name}"? This cannot be undone.
                        </p>
                        <div className="flex justify-end gap-3">
                            <button
                                onClick={() => setShowDeleteConfirm(false)}
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleDeleteConfirm}
                                disabled={deleteLoading}
                                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                            >
                                {deleteLoading ? <ButtonSpinner /> : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default PlayerDetailsPage;
